import { parseIssuerCode } from "@/entities/payment/payment.lib"
import { TFunction } from "i18next"
import {
  BillingInfo,
  ContractInfo,
  SubscriptionInfo,
} from "@/entities/account/account.types"
import { CurrencyUnitEnum } from "@/entities/payment/payment.types"
import {
  ApiSubscription,
  ApiSubscriptionStateEnum,
  AvailablePlans,
  SubscriptionStateEnum,
  TierIdEnum,
} from "./subscription.types"

export enum SubscriptionQueryKeysEnum {
  FetchUsage = "fetchUsage",
  ApiSubscription = "apiSubscription",
}

export const getQuota = (tierId: TierIdEnum): number => {
  switch (tierId) {
    case TierIdEnum.Inactive:
      return 5_000
    case TierIdEnum.Starter:
      return 10_000
    case TierIdEnum.Advanced:
      return 35_000
    case TierIdEnum.Ultimate:
      return 80_000
  }
}

export const getTierName = (quota: number): string => {
  switch (quota) {
    case 5_000:
    default:
      return "Free"
    case 10_000:
      return "Starter"
    case 35_000:
      return "Advanced"
    case 80_000:
      return "Ultimate"
  }
}

export const planToPrice: Record<
  TierIdEnum | SubscriptionStateEnum | string,
  number
> = {
  [TierIdEnum.Inactive]: 0,
  Free: 0,
  [SubscriptionStateEnum.Active]: 0,
  [SubscriptionStateEnum.MarkedToBeInactive]: 0,
  [TierIdEnum.Starter]: 10,
  [SubscriptionStateEnum.MarkedToBeStarter]: 10,
  [TierIdEnum.Advanced]: 30,
  [SubscriptionStateEnum.MarkedToBeAdvanced]: 30,
  [TierIdEnum.Ultimate]: 50,
}

export const getAvailablePlans = (t: TFunction): AvailablePlans[] => {
  return [
    {
      label: "Free",
      slug: TierIdEnum.Inactive,
      price: "0",
      features: [t("plan.planFeatureFree"), t("plan.planFeature2")],
      checked: false,
    },
    {
      label: "Starter",
      slug: TierIdEnum.Starter,
      price: "10",
      features: [
        t("plan.planFeatureStarter"),
        t("plan.planFeature2"),
        t("plan.planFeature3"),
        t("plan.planFeature4"),
        t("plan.planFeature5"),
      ],
      checked: false,
    },
    {
      label: "Advanced",
      slug: TierIdEnum.Advanced,
      price: "30",
      features: [
        t("plan.planFeatureAdvanced"),
        t("plan.planFeature2"),
        t("plan.planFeature3"),
        t("plan.planFeature4"),
        t("plan.planFeature5"),
      ],
      checked: false,
    },
    {
      label: "Ultimate",
      slug: TierIdEnum.Ultimate,
      price: "50",
      features: [
        t("plan.planFeatureUltimate"),
        t("plan.planFeature2"),
        t("plan.planFeature3"),
        t("plan.planFeature4"),
        t("plan.planFeature5"),
      ],
      checked: true,
    },
  ]
}

export const getTooltipMessage = (t: TFunction, currency: CurrencyUnitEnum) => {
  return [
    "",
    t("plan.tooltip1", {
      currency,
      price: currency === CurrencyUnitEnum.Usd ? "0.04" : "4",
    }),
    t("plan.tooltip2"),
    t("plan.tooltip3"),
  ]
}

export const getTableData = (t: TFunction) => {
  return [
    {
      tier: "Free",
      price: "0",
      words: "5,000",
      features: [
        t("plan.tableData1"),
        t("plan.tableData2"),
        t("plan.tableData3"),
        t("plan.tableData4"),
      ],
    },
    {
      tier: TierIdEnum.Starter,
      price: "10",
      words: "10,000",
      features: [
        t("plan.tableData1"),
        t("plan.tableData2"),
        t("plan.tableData3"),
        t("plan.tableData4"),
      ],
    },
    {
      tier: TierIdEnum.Advanced,
      price: "30",
      words: "35,000",
      features: [
        t("plan.tableData1"),
        t("plan.tableData2"),
        t("plan.tableData3"),
        t("plan.tableData4"),
      ],
    },
    {
      tier: TierIdEnum.Ultimate,
      price: "50",
      words: "80,000",
      features: [
        t("plan.tableData1"),
        t("plan.tableData2"),
        t("plan.tableData3"),
        t("plan.tableData4"),
      ],
    },
  ]
}

export const PAGE_COUNT_LIST: Record<string, number> = {
  Free: 20,
  Starter: 40,
  Advanced: 140,
  Ultimate: 320,
}

export const getMessageForSubscriptionState = (
  state: SubscriptionStateEnum,
  t: TFunction
) => {
  switch (state) {
    case SubscriptionStateEnum.MarkedToBeStarter:
      return t("settings.markedToBeStarter")
    case SubscriptionStateEnum.MarkedToBeAdvanced:
      return t("settings.markedToBeAdvanced")
    case SubscriptionStateEnum.MarkedToBeInactive:
      return t("settings.markedToBeInactive")
    default:
      return ""
  }
}

export const renderButtonText = (
  state: SubscriptionStateEnum,
  t: TFunction
) => {
  switch (state) {
    case SubscriptionStateEnum.MarkedToBeInactive:
      return t("settings.undoTermination")
    case SubscriptionStateEnum.MarkedToBeAdvanced:
    case SubscriptionStateEnum.MarkedToBeStarter:
      return t("settings.undoDowngrade")
    default:
      return t("settings.changePlan")
  }
}

export const renderMessageForApiSubscriptionState = (
  state: ApiSubscriptionStateEnum,
  t: TFunction
) => {
  switch (state) {
    case ApiSubscriptionStateEnum.Active:
      return t("settings.active")
    case ApiSubscriptionStateEnum.Inactive:
      return t("settings.inactive")
    case ApiSubscriptionStateEnum.MarkedToBeInactive:
      return t("settings.markedToBeInactiveState")
  }
}

export const renderNextPeriodPlan = (subscriptionInfo: SubscriptionInfo) => {
  switch (subscriptionInfo.state) {
    case SubscriptionStateEnum.MarkedToBeAdvanced:
      return TierIdEnum.Advanced
    case SubscriptionStateEnum.MarkedToBeStarter:
      return TierIdEnum.Starter
    case SubscriptionStateEnum.Inactive:
      return null
    case SubscriptionStateEnum.Active:
      return subscriptionInfo.tierId
    case SubscriptionStateEnum.MarkedToBeInactive:
      return null
  }
}

export const getNextTierCharge = (
  subscriptionInfo: SubscriptionInfo
): number => {
  switch (subscriptionInfo.state) {
    case SubscriptionStateEnum.Active:
      return planToPrice[subscriptionInfo.tierId]
    case SubscriptionStateEnum.MarkedToBeAdvanced:
      return planToPrice[SubscriptionStateEnum.MarkedToBeAdvanced]
    case SubscriptionStateEnum.MarkedToBeInactive:
      return 0
    case SubscriptionStateEnum.MarkedToBeStarter:
      return planToPrice[SubscriptionStateEnum.MarkedToBeStarter]
    case SubscriptionStateEnum.Inactive:
      return 0
  }
}

export const getWhetherUserIsOtc = (contractInfo: ContractInfo) => {
  return contractInfo && contractInfo.subscriptionId === null
}

export const getQuotaByContractInfo = (contractInfo: ContractInfo) => {
  return getWhetherUserIsOtc(contractInfo) ? 5_000 : Number(contractInfo?.quota)
}

export const getWhetherUserIsUnlimited = (contractInfo: ContractInfo) => {
  return (
    contractInfo.isLimited === false && contractInfo.subscriptionId === null
  )
}

export const isLimitExceeded = (
  contractInfo: ContractInfo,
  accumulatedCount: string
) => {
  return getWhetherUserIsOtc(contractInfo)
    ? false
    : Number(accumulatedCount) > Number(contractInfo?.quota)
}

export const getWhetherUserIsGloballyPaid = (billingInfo: BillingInfo) => {
  return !!billingInfo?.cardInfo?.last4 || billingInfo?.currency === "usd"
}

export const getIssuer = (billingInfo: BillingInfo) => {
  return getWhetherUserIsGloballyPaid(billingInfo)
    ? (billingInfo.cardInfo?.brand ?? "")
    : parseIssuerCode(billingInfo.cardInfo?.issuerCode ?? "")
}

export const getWhetherUserIsApiSubscribed = (
  subscriptionList: ApiSubscription[]
): boolean => {
  return subscriptionList && subscriptionList.length > 0
    ? subscriptionList.filter((subscription) =>
        [
          ApiSubscriptionStateEnum.Active,
          ApiSubscriptionStateEnum.MarkedToBeInactive,
        ].includes(subscription.state)
      ).length > 0
    : false
}

export const getWhetherUserReceivedCancellation = (
  subscriptionInfo: SubscriptionInfo
) => {
  return subscriptionInfo.state === SubscriptionStateEnum.MarkedToBeInactive
}

export const API_BASE_PRICE = 6.272
